import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Car, Fuel, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

interface MonthlyKmData {
  month: string;
  monthLabel: string;
  totalKm: number;
  electricKm: number;
  fuelKm: number;
}

const VehicleKilometerOverview = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<MonthlyKmData[]>([]);
  const [totals, setTotals] = useState({ total: 0, electric: 0, fuel: 0 });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load vehicles to determine energy type
        const { data: vehicles, error: vErr } = await (supabase as any)
          .from("vehicles")
          .select("id, license_plate, vehicle_type");

        if (vErr) throw vErr;

        // Build a set of electric vehicle IDs (plates ending with E)
        const electricVehicleIds = new Set<string>();
        (vehicles || []).forEach((v: any) => {
          const plate = (v.license_plate || "").trim().toUpperCase();
          if (plate.endsWith("E")) {
            electricVehicleIds.add(v.id);
          }
        });

        // Load odometer readings
        const { data: readings, error: rErr } = await (supabase as any)
          .from("vehicle_odometer_readings")
          .select("vehicle_id, reading_date, kilometers_this_month")
          .not("kilometers_this_month", "is", null)
          .order("reading_date", { ascending: true });

        if (rErr) throw rErr;

        // Aggregate by month
        const monthMap = new Map<string, { electric: number; fuel: number }>();

        (readings || []).forEach((r: any) => {
          const month = String(r.reading_date).substring(0, 7); // YYYY-MM
          const km = Number(r.kilometers_this_month) || 0;
          const isElectric = electricVehicleIds.has(r.vehicle_id);

          if (!monthMap.has(month)) {
            monthMap.set(month, { electric: 0, fuel: 0 });
          }
          const entry = monthMap.get(month)!;
          if (isElectric) {
            entry.electric += km;
          } else {
            entry.fuel += km;
          }
        });

        // Convert to sorted array
        const chartData: MonthlyKmData[] = Array.from(monthMap.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([month, vals]) => ({
            month,
            monthLabel: new Date(month + "-01").toLocaleDateString("de-DE", {
              month: "short",
              year: "numeric",
            }),
            totalKm: vals.electric + vals.fuel,
            electricKm: vals.electric,
            fuelKm: vals.fuel,
          }));

        setData(chartData);

        // Calculate totals
        const totalElectric = chartData.reduce((s, d) => s + d.electricKm, 0);
        const totalFuel = chartData.reduce((s, d) => s + d.fuelKm, 0);
        setTotals({
          total: totalElectric + totalFuel,
          electric: totalElectric,
          fuel: totalFuel,
        });
      } catch (error) {
        console.error("Error loading km data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Car className="h-5 w-5" />
            Monatliche Kilometer-Übersicht
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">Noch keine Tachostand-Daten vorhanden.</p>
        </CardContent>
      </Card>
    );
  }

  const formatKm = (value: number) => value.toLocaleString("de-DE") + " km";

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Gesamt Kilometer</CardTitle>
            <Car className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totals.total.toLocaleString("de-DE")} km</div>
            <p className="text-xs text-muted-foreground">Über alle Monate</p>
          </CardContent>
        </Card>

        <Card className="border-chart-3/30 bg-chart-3/5">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Elektro-Kilometer</CardTitle>
            <Zap className="h-4 w-4 text-chart-3" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-chart-3">
              {totals.electric.toLocaleString("de-DE")} km
            </div>
            <p className="text-xs text-muted-foreground">
              {totals.total > 0
                ? `${((totals.electric / totals.total) * 100).toFixed(1)}% der Gesamtkilometer`
                : "Keine Daten"}
            </p>
          </CardContent>
        </Card>

        <Card className="border-chart-1/30 bg-chart-1/5">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Verbrenner-Kilometer</CardTitle>
            <Fuel className="h-4 w-4 text-chart-1" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-chart-1">
              {totals.fuel.toLocaleString("de-DE")} km
            </div>
            <p className="text-xs text-muted-foreground">
              {totals.total > 0
                ? `${((totals.fuel / totals.total) * 100).toFixed(1)}% der Gesamtkilometer`
                : "Keine Daten"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Stacked Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Car className="h-5 w-5" />
            Monatliche Kilometer-Übersicht
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Aufgliederung nach Elektro- und Verbrennerfahrzeugen
          </p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthLabel" tick={{ fontSize: 12 }} />
              <YAxis
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: number, name: string) => [
                  formatKm(value),
                  name === "electricKm" ? "Elektro" : "Verbrenner",
                ]}
                labelFormatter={(label) => `Monat: ${label}`}
              />
              <Legend
                formatter={(value) =>
                  value === "electricKm" ? "⚡ Elektro" : "⛽ Verbrenner"
                }
              />
              <Bar
                dataKey="fuelKm"
                stackId="km"
                fill="hsl(var(--chart-1))"
                name="fuelKm"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="electricKm"
                stackId="km"
                fill="hsl(var(--chart-3))"
                name="electricKm"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default VehicleKilometerOverview;
